import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, Check } from 'lucide-react';
import api from '../lib/api';

const RGPDModal = ({ client, onClose, onUpdate }) => {
  const sigCanvas = useRef({});
  const [adsConsent, setAdsConsent] = useState(client?.ads_consent ?? true);
  const [skinType, setSkinType] = useState(client?.skin_type || 'EUROPEO NORMAL');
  const [dni, setDni] = useState(client?.dni || '');
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 200 });

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setCanvasSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    // Si ya existe una firma almacenada, la precargamos al abrir el modal para que pueda visualizarla.
    if (client?.signature && sigCanvas.current) {
      setTimeout(() => {
        if (sigCanvas.current && sigCanvas.current.fromDataURL) {
          sigCanvas.current.fromDataURL(client.signature);
        }
      }, 100);
    }
  }, [client]);

  const saveSignature = async () => {
    if (sigCanvas.current.isEmpty()) {
      alert("Por favor provea una firma");
      return;
    }
    
    // Validar si puso un DNI en caso de que no existiera
    if (!dni.trim()) {
      alert("Por favor, introduzca el DNI del cliente.");
      return;
    }

    setLoading(true);
    const signatureData = sigCanvas.current.getCanvas().toDataURL('image/png');
    
    try {
      await api.put(`/clients/${client.id}`, { 
        rgpd_status: 'Acepto', 
        signature: signatureData,
        ads_consent: adsConsent,
        skin_type: skinType,
        dni: dni
      });
      
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error("Error saving signature", error);
      alert("Error al guardar. Revise la consola.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/50 z-[60] flex items-center justify-center p-2 sm:p-4 py-4 sm:py-8">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-full overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-stone-100 flex items-center justify-between shrink-0 bg-white">
          <h3 className="font-semibold text-xl text-stone-800 text-center w-full">Añadir RGPD</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 absolute right-6">
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 sm:p-6 md:p-8 overflow-y-auto space-y-6 sm:space-y-8 bg-white flex-1 custom-scrollbar">
          
          <div className="text-center font-bold text-lg text-stone-800 uppercase tracking-wide">
            {client?.name}
          </div>

          <div>
            <input 
              type="text" 
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              placeholder="DNI / NIE"
              className="w-full border border-stone-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-center text-lg text-stone-800">Protección de Datos</h4>
            <p className="text-sm text-stone-600 text-justify leading-relaxed">
              Se le informa que el responsable del tratamiento de sus datos es MÓNICA AROZAMENA GARCIA (SOL PLANET). Sus datos serán tratados con la finalidad de gestionar el servicio solicitado, concertar citas y facturación de nuestros servicios. La legitimación para el uso de sus datos está basada en la ejecución de un contrato o prestación de servicios. No se cederán datos a terceros, salvo obligación legal. Los datos serán destruidos una vez comunique su baja y/o finalizados los periodos legales de conservación. No se llevarán a cabo transferencias internacionales de datos ni análisis de perfiles. Sus datos podrán ser cedidos para las indicadas finalidades que puedan ser realizadas entre la entidad y otras sociedades relacionadas con la prestación de los servicios solicitados o auxiliares de estos de los términos previstos en la indicada Ley. Los interesados podrán ejercer sus derechos de acceso, rectificación, supresión, oposición, portabilidad o limitación del tratamiento, dirigiéndose a MÓNICA AROZAMENA GARCÍA (SOL PLANET) en la siguiente dirección: PLAZA ARGENTA, BAJO 39700, CASTRO URDIALES. Igualmente, puede presentar una reclamación ante la Agencia Española de Protección de datos si considera que sus derechos han sido vulnerados. En el caso de producirse alguna modificación de sus datos, rogamos nos lo comunique debidamente por escrito con la finalidad de mantener sus datos actualizados.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm text-stone-700 font-medium">
              Autorizo el uso de mis datos de carácter personal para el envío de información publicitaria, avisos de citas por email, sms o WhatsApp
            </p>
            <button 
              onClick={() => setAdsConsent(!adsConsent)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${adsConsent ? 'bg-emerald-500' : 'bg-stone-300'}`}
            >
              <span className={`inline-block w-full text-xs font-bold text-white transition-all ${adsConsent ? 'text-left pl-2' : 'text-right pr-2'}`}>
                {adsConsent ? 'SI' : 'NO'}
              </span>
              <span className={`absolute h-6 w-6 transform rounded-full bg-white transition-transform ${adsConsent ? 'translate-x-[36px]' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-center text-lg text-stone-800">Información para un bronceado responsable</h4>
            <ol className="list-decimal pl-5 text-sm text-stone-600 space-y-2 text-justify">
              <li>Es obligatorio utilizar gafas frente a las radiaciones ultravioletas emitidas por los aparatos de bronceado para evitar lesiones oculares.</li>
              <li>No exponerse al sol y al solarium el mismo día.</li>
              <li>Respetar cuarenta y ocho horas entre las dos primeras sesiones.</li>
              <li>El exceso de radiaciones ultravioletas puede afectar gravemente a la piel y a los ojos. Las exposiciones intensas y repetidas pueden provocar envejecimiento prematuro de la piel, así como un aumento del riesgo de desarrollar un cáncer de piel, los daños a la piel son irreversibles.</li>
              <li>Deben tomarse las precauciones necesarias en los periodos de tratamiento con ciertos medicamentos, entre otros: antibióticos, somníferos, antidepresivos, antisépticos locales o generales, aumentan la sensibilidad a las radiaciones, así como los cosméticos. En los periodos de tratamiento evite las exposiciones a las radiaciones ultravioletas. En caso de duda consulte a su médico.</li>
              <li>Retirar bien los cosméticos antes de su exposición. No aplicar ningún filtro solar.</li>
              <li>Las radiaciones UV pueden ser especially peligrosas en personas de piel muy clara y no deben se utilizadas por personas que se queman sin broncearse, que presentan insolación, que hayan tenido cáncer de piel o condiciones que predispongan a dicho cáncer. Las personas que tengan antecedentes de cáncer de piel deben evitar también su utilización.</li>
              <li>Seguir las recomendaciones relativas a la duración en intensidad de la exposición, así como a la distancia de la lámpara.</li>
              <li>Consultar al médico si se desarrollan ampollas, heridas o enrojecimiento de la piel.</li>
              <li>La exposición a las rayos ultravioletas están prohibidas a los menores de 18 años y desaconsejada a las mujeres embarazadas.</li>
            </ol>
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-center text-lg text-stone-800">Fototipos de piel</h4>
            
            <div className="space-y-6 text-sm text-stone-700">
              
              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="radio" name="skinType" value="NÓRDICO" checked={skinType === 'NÓRDICO'} onChange={(e) => setSkinType(e.target.value)} className="mt-1" />
                <div>
                  <span className="font-semibold block mb-1">NÓRDICO</span>
                  <ul className="list-disc pl-5 text-stone-500 mb-1">
                    <li>Piel muy clara</li>
                    <li>Pelo rojizo</li>
                    <li>Ojos azules</li>
                    <li>Siempre se quema</li>
                  </ul>
                  <span className="text-stone-800">No debería usar solariums</span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="radio" name="skinType" value="EUROPEO CLARO" checked={skinType === 'EUROPEO CLARO'} onChange={(e) => setSkinType(e.target.value)} className="mt-1" />
                <div>
                  <span className="font-semibold block mb-1">EUROPEO CLARO</span>
                  <ul className="list-disc pl-5 text-stone-500 mb-1">
                    <li>Piel muy clara</li>
                    <li>Pelo rubio/castaño claro</li>
                    <li>Ojos azules/verdes/grises</li>
                    <li>Casi siempre se quema</li>
                  </ul>
                  <span className="text-stone-800">Debe controlar su bronceado</span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="radio" name="skinType" value="EUROPEO NORMAL" checked={skinType === 'EUROPEO NORMAL'} onChange={(e) => setSkinType(e.target.value)} className="mt-1" />
                <div>
                  <span className="font-semibold block mb-1 text-blue-600">EUROPEO NORMAL</span>
                  <ul className="list-disc pl-5 text-stone-500 mb-1">
                    <li>Piel normal</li>
                    <li>Pelo rubio oscuro/marrón</li>
                    <li>Ojos grises/marrones</li>
                  </ul>
                  <span className="text-stone-800">Se puede broncear sin problemas, respetando los tiempos de exposición.</span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="radio" name="skinType" value="MEDITERRÁNEO" checked={skinType === 'MEDITERRÁNEO'} onChange={(e) => setSkinType(e.target.value)} className="mt-1" />
                <div>
                  <span className="font-semibold block mb-1">MEDITERRÁNEO</span>
                  <ul className="list-disc pl-5 text-stone-500 mb-1">
                    <li>Piel morena clara/olivacea</li>
                    <li>Pelo oscuro</li>
                    <li>Ojos oscuros</li>
                  </ul>
                  <span className="text-stone-800">Se broncea sin problemas.</span>
                </div>
              </label>

            </div>
          </div>

          <div className="space-y-4 pt-6 border-t border-stone-100 flex flex-col items-center">
            <h4 className="font-medium text-stone-800">Firma</h4>
            <div className="flex gap-4">
              <button 
                onClick={() => sigCanvas.current.clear()}
                className="px-3 py-1 text-xs border border-stone-300 rounded text-stone-600 hover:bg-stone-50"
              >
                Limpiar y firmar de nuevo
              </button>
            </div>
            <div 
              ref={containerRef}
              className="bg-white border-2 border-stone-800 w-full max-w-full aspect-[4/2.1] overflow-hidden shadow-inner relative"
            >
              <SignatureCanvas 
                ref={sigCanvas}
                penColor="#000000"
                canvasProps={{
                  width: canvasSize.width,
                  height: canvasSize.height,
                  className: 'sigCanvas'
                }} 
              />
            </div>
          </div>

        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-stone-100 flex flex-col gap-3 bg-white shrink-0">
          <button 
            onClick={saveSignature}
            disabled={loading}
            className="w-full bg-blue-500 text-white py-3 rounded-lg font-bold hover:bg-blue-600 transition flex items-center justify-center gap-2"
          >
            {loading ? "Guardando..." : "Guardar"}
          </button>
          <button 
            onClick={onClose}
            className="w-full text-stone-600 py-3 font-medium hover:text-stone-800 transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RGPDModal;
